import type { Media } from "@/payload-types";

/**
 * Media Utility Functions
 * 
 * Simple helpers for working with Payload Media documents
 * without creating unnecessary abstraction layers.
 */

/**
 * Safely extract media data from a Payload Media document
 * 
 * @param media - Media document from Payload (can be object, number, or null)
 * @returns Normalized media data or null if invalid
 */
export function getMediaData(media: Media | string | number | null | undefined) {
  // Handle null, undefined, or ID-only references
  if (!media || typeof media !== 'object') return null;
  
  // Ensure we have a valid URL
  if (!media.url) return null;
  
  return {
    src: media.url,
    alt: media.alt || '', // Alt is required in our collection, but handle edge cases
    caption: media.caption,
    width: media.width || undefined,
    height: media.height || undefined,
    mimeType: media.mimeType,
    sizes: media.sizes,
  };
}

/**
 * Get a specific size variant of an image
 * 
 * @param media - Media document from Payload
 * @param size - Size variant name (thumbnail, mobile, tablet, desktop, hero, og)
 * @returns URL of the sized image or original URL if size not found
 */
export function getMediaSize(
  media: Media | string | number | null | undefined,
  size: 'thumbnail' | 'mobile' | 'tablet' | 'desktop' | 'hero' | 'og'
): string | null {
  const data = getMediaData(media);
  if (!data) return null;
  
  // Check if the requested size exists
  if (data.sizes && data.sizes[size]?.url) {
    return data.sizes[size].url;
  }
  
  // Fall back to original
  return data.src;
}

/**
 * Get responsive sizes attribute for Next.js Image
 * Based on common use cases in the application
 *
 * @param context - Where the image is being used
 * @returns Sizes string for responsive loading
 */
export function getImageSizes(
  context: 'hero' | 'card' | 'content' | 'thumbnail' | 'full'
): string {
  const sizes = {
    hero: '100vw',
    card: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
    content: '(max-width: 768px) 100vw, (max-width: 1200px) 66vw, 50vw',
    thumbnail: '(max-width: 640px) 50vw, 200px',
    full: '100vw',
  };

  return sizes[context] || sizes.content;
}

/**
 * CSS object-position value from a Media doc's focal point
 *
 * Pair with `object-cover`: the focalX%/focalY% point of the image is pinned
 * to the same point of the container, keeping the editor-chosen subject
 * (e.g. a face) in frame regardless of the container's aspect ratio.
 *
 * @param media - Payload media field (can be Media object, string ID, null, or undefined)
 * @returns object-position string, or undefined when no focal point is set
 *          (browser default 50% 50% — identical to previous center-crop behavior)
 *
 * @example
 * <Image src={url} alt={alt} fill className="object-cover" style={{ objectPosition: getFocalPosition(image) }} />
 */
export function getFocalPosition(
  media: Media | string | number | null | undefined
): string | undefined {
  if (!media || typeof media !== "object") return undefined;
  const { focalX, focalY } = media;
  if (typeof focalX !== "number" || typeof focalY !== "number") return undefined;
  return `${focalX}% ${focalY}%`;
}

/**
 * Extract image URL and alt text from Payload media field
 *
 * Simplified helper for the common pattern of extracting just URL and alt text.
 * This centralizes the repeated pattern across block renderers.
 *
 * @param media - Payload media field (can be Media object, string ID, null, or undefined)
 * @param fallbackAlt - Fallback alt text if media doesn't have one (default: "")
 * @returns Object with url, alt, and objectPosition properties
 *
 * @example
 * // Replace this 3-line pattern:
 * const imageData = typeof image === "object" ? image : null;
 * const imageUrl = imageData?.url || null;
 * const imageAlt = imageData?.alt || heading;
 *
 * // With this 1-line call:
 * const { url: imageUrl, alt: imageAlt } = getImageData(image, heading);
 */
export function getImageData(
  media: Media | string | number | null | undefined,
  fallbackAlt: string = ""
) {
  // Only process if media is an object (populated Media type)
  const imageData = typeof media === "object" && media !== null ? media : null;

  return {
    url: imageData?.url || null,
    alt: imageData?.alt || fallbackAlt,
    objectPosition: getFocalPosition(imageData),
  };
}